import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppToast } from "@/hooks/useAppToast";
import {
  getStudentLevelTestSession,
  deleteStudentLevelTestSession,
} from "@/services/admin/studentLevelTest.service";
import { LevelTestSession } from "@/services/admin/studentLevelTest.interfaces";
import {
  hasTimelineData,
  buildTimelineFromSession,
  type TimelineEvent,
} from "@/services/admin/levelTestTimeline";
import {
  hasPerformanceData,
  buildPerformanceViewFromSession,
  type PerformanceLevelSection,
} from "@/services/admin/levelTestPerformance";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { getStudentById } from "@/services/admin/student.service";

const BackButton: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className="mb-6 flex items-center space-x-4">
    <button
      onClick={onBack}
      className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-900"
    >
      <i className="fa-solid fa-arrow-left"></i>
      <span>Voltar ao estudante</span>
    </button>
  </div>
);

interface SessionCardProps {
  session: LevelTestSession;
  onDelete: () => void;
}

const SessionCard: React.FC<SessionCardProps> = ({ session, onDelete }) => {
  const [activeTab, setActiveTab] = useState<"timeline" | "performance">("timeline");

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800";
      case "Active":
        return "bg-blue-100 text-blue-800";
      case "Skipped":
        return "bg-amber-100 text-amber-900";
      case "Aborted":
        return "bg-red-100 text-red-800";
      case "Expired":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const statusLabel =
    session.status === "Completed"
      ? "Concluída"
      : session.status === "Active"
        ? "Ativa"
        : session.status === "Skipped"
          ? "Pulado"
          : session.status === "Aborted"
            ? "Interrompida"
            : session.status === "Expired"
              ? "Expirada"
              : session.status;

  const timelineAvailable = hasTimelineData(session);
  const timelineEventsCount = timelineAvailable
    ? buildTimelineFromSession(session).length
    : 0;
  const performanceAvailable = hasPerformanceData(session);
  const performanceSections = performanceAvailable
    ? buildPerformanceViewFromSession(session)
    : [];

  return (
    <div className="space-y-6">
      {/* Informações Gerais */}
      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Informações da sessão</h2>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(
              session.status
            )}`}
          >
            {statusLabel}
          </span>
        </div>

        {session.status === "Skipped" && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            O estudante optou por pular o teste de nivelamento. A colocação foi definida como nível inicial
            (módulos básicos desbloqueados). Não há respostas nem desempenho por questão para esta sessão.
            {hasPerformanceData(session) && (
              <span className="mt-2 block text-amber-900">
                A lista de módulos liberados por esta sessão está na aba <strong>Desempenho</strong>.
              </span>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-neutral-500">Iniciado em</p>
            <p className="text-sm font-medium">
              {new Date(session.startedAt).toLocaleString()}
            </p>
          </div>
          {session.completedAt && (
            <div>
              <p className="text-sm text-neutral-500">Concluído em</p>
              <p className="text-sm font-medium">
                {new Date(session.completedAt).toLocaleString()}
              </p>
            </div>
          )}
          {session.finalLevel && (
            <div>
<p className="text-sm text-neutral-500">Nível final</p>
            <p className="text-sm font-medium">
              Nível {session.finalLevel}
                {session.finalLevelName && ` - ${session.finalLevelName}`}
              </p>
            </div>
          )}
          {session.score != null && (
            <div>
              <p className="text-sm text-neutral-500">Pontuação</p>
              <p className="text-sm font-medium">
                {session.score.toFixed(1)}%
                {session.scorePercentage != null &&
                  ` (${session.scorePercentage.toFixed(1)}%)`}
              </p>
            </div>
          )}
          {session.correctAnswers != null && (
            <div>
<p className="text-sm text-neutral-500">Respostas corretas</p>
            <p className="text-sm font-medium">
              {session.correctAnswers} / {session.answers?.length ?? 0}
              </p>
            </div>
          )}
        </div>

        {/* Skill Breakdown */}
        {session.skillBreakdown && Object.keys(session.skillBreakdown).length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-medium mb-3">Desempenho por habilidade</h3>
            <div className="space-y-2">
              {Object.entries(session.skillBreakdown).map(([skill, score]) => (
                <div key={skill} className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600">{skill}</span>
                  <span className="text-sm font-medium">{score != null ? score.toFixed(1) : 0}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 border-t pt-4 flex justify-end">
          <Button variant="destructive" onClick={onDelete}>
            <i className="fa-solid fa-trash mr-2"></i>
            Excluir sessão
          </Button>
        </div>
      </div>

      {/* Respostas e Linha do tempo em abas */}
      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-neutral-900">Detalhes da sessão</h3>
            <p className="text-sm text-neutral-500">
              Navegue pelas respostas e eventos da linha do tempo nas abas abaixo.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-medium text-neutral-700">
              {session.answers.length} respostas
            </span>
            <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-medium text-neutral-700">
              {timelineEventsCount} eventos na linha do tempo
            </span>
            {performanceAvailable && (
              <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-medium text-neutral-700">
                {session.status === "Skipped"
                  ? `${session.performanceSummary?.unlockedModules?.length ?? 0} módulos desbloqueados`
                  : `${performanceSections.length} níveis`}
              </span>
            )}
          </div>
        </div>
        <div role="tablist" aria-label="Abas da sessão do teste de nivelamento" className="mb-5 grid h-10 w-full max-w-2xl grid-cols-2 rounded-md bg-gray-100 p-1">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "timeline"}
            aria-controls="level-test-timeline-panel"
            onClick={() => setActiveTab("timeline")}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 ${
              activeTab === "timeline"
                ? "bg-white text-gray-950 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="fa-solid fa-history text-sm" aria-hidden />
            Linha do tempo
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "performance"}
            aria-controls="level-test-performance-panel"
            onClick={() => setActiveTab("performance")}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 ${
              activeTab === "performance"
                ? "bg-white text-gray-950 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className="fa-solid fa-chart-column text-sm" aria-hidden />
            Desempenho
          </button>
        </div>
        {activeTab === "timeline" && (
          <div id="level-test-timeline-panel" role="tabpanel" className="mt-0">
            {timelineAvailable ? (
              <LevelTestTimelineView session={session} />
            ) : (
              <p className="text-sm text-neutral-500 py-8">Linha do tempo não disponível</p>
            )}
          </div>
        )}
        {activeTab === "performance" && (
          <div id="level-test-performance-panel" role="tabpanel" className="mt-0">
            {performanceAvailable ? (
              <LevelTestPerformanceView sections={performanceSections} />
            ) : (
              <p className="text-sm text-neutral-500 py-8">
                {session.status === "Skipped"
                  ? "Teste pulado: não há respostas. Se a lista de módulos não aparecer, atualize o backend ou recarregue a página."
                  : "Conclua o teste para ver o desempenho por nível."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const LevelTestTimelineView: React.FC<{ session: LevelTestSession }> = ({ session }) => {
  const events = buildTimelineFromSession(session);
  if (events.length === 0) {
    return (
      <p className="text-sm text-neutral-500 py-8 text-center">Nenhum evento para exibir</p>
    );
  }
  return (
    <div className="relative space-y-0">
      <div className="pointer-events-none absolute bottom-0 left-4 top-0 border-l-2 border-dashed border-neutral-200" />
      {events.map((event, index) => (
        <TimelineEventRow
          key={`${event.type}-${event.sequence}-${index}`}
          event={event}
          isLast={index === events.length - 1}
        />
      ))}
    </div>
  );
};

const LevelTestPerformanceView: React.FC<{ sections: PerformanceLevelSection[] }> = ({
  sections,
}) => {
  if (sections.length === 0) {
    return (
      <p className="text-sm text-neutral-500 py-8 text-center">Nenhum dado de nível para exibir</p>
    );
  }

  const skipSection = sections.find((s) => s.isSkipPlacement);
  if (skipSection) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-5">
        <h4 className="text-base font-semibold text-amber-950">
          Módulos desbloqueados (teste pulado)
        </h4>
        <p className="mt-2 text-sm text-amber-900">
          Colocação inicial — nível {skipSection.level}. Estes módulos foram liberados porque o estudante
          optou por pular o teste de nivelamento (política de baseline do sistema), não por desempenho nas
          questões.
        </p>
        {skipSection.unlockedModules.length === 0 ? (
          <p className="mt-4 text-sm text-neutral-600">
            Nenhum módulo está registado para esta sessão na base de dados.
          </p>
        ) : (
          <ul className="mt-4 flex flex-wrap gap-2" aria-label="Módulos desbloqueados">
            {skipSection.unlockedModules.map((m) => (
              <li
                key={m.id}
                className="rounded-md border border-amber-200 bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-950"
              >
                {m.name ?? m.internalName ?? m.id}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="relative space-y-8">
      <div className="pointer-events-none absolute bottom-0 left-8 top-0 border-l-2 border-dashed border-neutral-200" />
      {sections.map((section) => (
        <div
          key={`level-${section.level}`}
          className={`rounded-lg border p-4 ${
            section.isWorst ? "border-amber-300 bg-amber-50/50" : "border-neutral-200 bg-white"
          }`}
        >
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <span
              className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-600"
              aria-hidden
            >
              <i className="fa-solid fa-layer-group text-sm" />
            </span>
            <h4 className="text-base font-semibold text-neutral-900">
              Nível {section.level}
            </h4>
            <span className="inline-flex items-center rounded bg-neutral-100 px-2 py-0.5 text-sm font-medium text-neutral-700">
              {section.accuracyPercent}% corretas
            </span>
            {section.isWorst && (
              <span className="inline-flex items-center rounded bg-amber-200 px-2 py-0.5 text-sm font-semibold text-amber-900">
                Pior desempenho — define os módulos desbloqueados
              </span>
            )}
          </div>
          {section.isWorst && section.unlockedModules.length > 0 && (
            <div className="mb-4 rounded border border-amber-200 bg-amber-50/80 p-3">
              <p className="text-xs font-semibold text-amber-900 mb-2">
                Módulos desbloqueados ({section.unlockedModules.length}):
              </p>
              <ul className="flex flex-wrap gap-2 text-xs text-amber-800">
                {section.unlockedModules.map((m) => (
                  <li key={m.id} className="rounded bg-amber-100 px-2 py-1">
                    {m.name ?? m.internalName ?? m.id}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="relative space-y-0 pl-0">
            {section.questions.map((event, index) => (
              <TimelineEventRow
                key={`${section.level}-${event.exerciseId}-${index}`}
                event={event}
                isLast={index === section.questions.length - 1}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const timelineEventIcons: Record<TimelineEvent["type"], string> = {
  SessionStarted: "fa-play",
  PoolAllocated: "fa-layer-group",
  AnswerSubmitted: "fa-circle-check",
  Phase1Completed: "fa-flag-checkered",
  Phase2Started: "fa-forward",
  RoundAllocated: "fa-rotate",
  TestCompleted: "fa-trophy",
};

const TimelineEventRow: React.FC<{ event: TimelineEvent; isLast?: boolean }> = ({
  event,
  isLast = false,
}) => {
  const at = new Date(event.at).toLocaleString();
  const icon = timelineEventIcons[event.type];
  const iconColor =
    event.type === "TestCompleted"
      ? "text-amber-600 bg-amber-50"
      : event.type === "AnswerSubmitted"
        ? event.isCorrect
          ? "text-green-600 bg-green-50"
          : "text-red-600 bg-red-50"
        : "text-neutral-600 bg-neutral-100";

  const content = (() => {
    switch (event.type) {
      case "SessionStarted":
        return <span className="font-medium">Sessão iniciada</span>;
      case "PoolAllocated": {
        const range =
          event.minLevel != null && event.maxLevel != null
            ? `níveis ${event.minLevel}–${event.maxLevel}`
            : "";
        return (
          <>
            <span className="font-medium">Conjunto alocado</span>
            <span className="text-neutral-600 ml-2">
              {event.count} questões {range && `(${range})`}
            </span>
          </>
        );
      }
      case "AnswerSubmitted":
        return (
          <>
            <span className="font-medium">Questão {event.questionNumber}</span>
            <span className="basis-full text-xs text-neutral-500">{at}</span>
            {event.level != null && (
              <span className="text-neutral-500 ml-2 text-xs">Nível {event.level}</span>
            )}
            <span
              className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                event.isCorrect ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
              }`}
            >
              {event.isCorrect ? "Correto" : "Incorreto"}
            </span>
            <div className="basis-full mt-2 space-y-2 rounded border bg-neutral-50 p-3">
              {(event.exerciseTitle || event.exerciseDescription) && (
                <div>
                  {event.exerciseTitle && (
                    <p className="text-sm font-medium text-neutral-700">{event.exerciseTitle}</p>
                  )}
                  {event.exerciseDescription && (
                    <p className="mt-1 text-xs text-neutral-500">{event.exerciseDescription}</p>
                  )}
                </div>
              )}

              {event.questionText && (
                <p className="text-sm font-medium text-neutral-800">{event.questionText}</p>
              )}

              {Array.isArray(event.options) && event.options.length > 0 && (
                <div className="space-y-1">
                  {event.options.map((option) => {
                    const selectedAndCorrect = option.isSelected && event.isCorrect;
                    const selectedAndIncorrect = option.isSelected && !event.isCorrect;
                    return (
                      <div
                        key={`${event.exerciseId}-${option.order}-${option.text}`}
                        className={`rounded border-2 p-2 text-xs ${
                          selectedAndCorrect
                            ? "border-green-500 bg-green-200 font-semibold"
                            : selectedAndIncorrect
                              ? "border-red-500 bg-red-200 font-semibold"
                              : option.isCorrect && !option.isSelected
                                ? "border-green-300 bg-green-50"
                                : "border-neutral-200 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{option.order}.</span>
                          <span className="break-words">{option.text}</span>
                          {selectedAndCorrect && (
                            <span className="ml-auto text-xs font-bold text-green-800">
                              ✓ Selecionada e correta
                            </span>
                          )}
                          {selectedAndIncorrect && (
                            <span className="ml-auto text-xs font-bold text-red-800">
                              ✗ Selecionada e incorreta
                            </span>
                          )}
                          {!option.isSelected && option.isCorrect && (
                            <span className="ml-auto text-xs font-medium text-green-700">
                              ✓ Resposta correta
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-xs text-neutral-500 break-words">
                <span className="font-medium text-neutral-600">Resposta do estudante:</span>{" "}
                {event.answerLabel || "Sem resposta"}
              </p>
            </div>
          </>
        );
      case "Phase1Completed":
        return (
          <>
            <span className="font-medium">Fase 1 concluída</span>
            <span className="text-neutral-600 ml-2">
              {event.percentage}% ({event.correct}/{event.total}) → Grupo {event.phaseGroup}.{" "}
              {event.ruleText}
            </span>
          </>
        );
      case "Phase2Started": {
        const range =
          event.minLevel != null && event.maxLevel != null
            ? `níveis ${event.minLevel}–${event.maxLevel}`
            : "";
        return (
          <>
            <span className="font-medium">Fase 2 iniciada</span>
            <span className="text-neutral-600 ml-2">
              Grupo {event.phaseGroup} {range && `(${range})`}
            </span>
          </>
        );
      }
      case "RoundAllocated": {
        const range =
          event.minLevel != null && event.maxLevel != null
            ? `níveis ${event.minLevel}–${event.maxLevel}`
            : "";
        return (
          <>
            <span className="font-medium">Rodada {event.roundIndex} alocada</span>
            <span className="text-neutral-600 ml-2">
              {event.count} questões {range && `(${range})`}
            </span>
          </>
        );
      }
      case "TestCompleted":
        return (
          <>
            <span className="font-medium">Teste concluído</span>
            <span className="text-neutral-600 ml-2">
              Nível final: {event.finalLevel}
              {event.finalLevelName ? ` (${event.finalLevelName})` : ""}
            </span>
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <div className="relative flex gap-4 py-4">
      <div
        className={`flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-full ${iconColor}`}
        aria-hidden
      >
        <i className={`fa-solid ${icon} text-sm`} />
      </div>
      <div className={`flex-1 min-w-0 pt-0.5 ${!isLast ? "border-b border-neutral-100 pb-4" : ""}`}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {content}
        </div>
        {event.type !== "AnswerSubmitted" && (
          <p className="mt-1 text-xs text-neutral-500">{at}</p>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="rounded-lg border bg-white p-12 shadow-sm text-center">
    <i className="fa-solid fa-clipboard-question text-4xl text-neutral-400 mb-4"></i>
    <h3 className="text-lg font-semibold mb-2">Nenhuma sessão de teste de nivelamento</h3>
    <p className="text-sm text-neutral-600">
      Este estudante ainda não realizou o teste de nivelamento.
    </p>
  </div>
);

const AdminStudentLevelTestPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<LevelTestSession | null>(null);
  const [studentName, setStudentName] = useState<string>("Estudante");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!studentId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Carregar nome do estudante
        console.log("Loading student data for:", studentId);
        const student = await getStudentById(studentId);
        console.log("Student loaded:", student);
        setStudentName(student.name);

        // Carregar sessão de teste
        console.log("Loading level test session for:", studentId);
        const levelTestSession = await getStudentLevelTestSession(studentId);
        console.log("Level test session loaded:", levelTestSession);
        setSession(levelTestSession);
      } catch (error) {
        console.error("Error loading data:", error);
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          errorMessage = JSON.stringify(error);
        }
        toast.error(`Falha ao carregar dados do teste de nivelamento: ${errorMessage}`);
        console.error("Full error details:", {
          error,
          studentId,
          errorType: typeof error,
          errorString: String(error)
        });
        // Não navegar automaticamente, apenas mostrar erro
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const handleDelete = async () => {
    if (!studentId) return;

    setIsDeleting(true);
    try {
      await deleteStudentLevelTestSession(studentId);
      toast.success("Sessão do teste de nivelamento excluída com sucesso");
      setSession(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting session:", error);
      toast.error("Falha ao excluir a sessão do teste de nivelamento");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminPageLayout title="Carregando...">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-neutral-600">Carregando dados do teste de nivelamento...</p>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title={`Teste de nivelamento - ${studentName}`}>
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir sessão do teste de nivelamento</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir esta sessão do teste de nivelamento? Esta ação
              não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main className="mx-auto py-8">
        <BackButton onBack={() => navigate(`/admin/access/students/${studentId}`)} />
        <div className="space-y-6">
          {session ? (
            <SessionCard
              session={session}
              onDelete={() => setDeleteDialogOpen(true)}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </main>
    </AdminPageLayout>
  );
};

export default AdminStudentLevelTestPage;